import React, { useMemo, useState } from 'react';
import { T, SUBJECTS, profileKey } from '../shared/constants';
import { Card, ProgressBar } from '../shared/ui';
import { buildWeeklyReport } from '../../lib/weeklyReport.mjs';

// "This week" for a parent: one child's last 7 days, from the progress the app keeps per child profile.
// Families with several children can flip between them here without changing who is practising.

const read = (key, fallback) => { try { const v = JSON.parse(localStorage.getItem(key) || 'null'); return v ?? fallback; } catch (_) { return fallback; } };
const labelOf = (id) => SUBJECTS.find((s) => s.id === id)?.label || (id ? id.charAt(0).toUpperCase() + id.slice(1) : 'Practice');
const fmtDate = (ts) => new Date(ts).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });

function Stat({ value, label }) {
  return (
    <div style={{ flex: '1 1 120px', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: '12px 14px' }}>
      <div style={{ fontSize: 24, fontWeight: 800, color: T.text, lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 12, color: T.textMid, marginTop: 3 }}>{label}</div>
    </div>
  );
}

export default function WeeklyReport({ activeProfile, maxProfiles = 3 }) {
  const profiles = read('lumio_profiles', []).slice(0, Math.max(1, maxProfiles)); // only the children the plan covers
  const [childId, setChildId] = useState(activeProfile?.id ?? (profiles[0]?.id ?? null));
  const child = profiles.find((p) => p.id === childId) || activeProfile || null;
  const pid = child ? child.id : null;

  const r = useMemo(() => buildWeeklyReport({
    activity: read(profileKey('lumio_activity', pid), []),
    glowup: read(profileKey('lumio_glowup', pid), null),
    progress: read(profileKey('lumio_progress_v2', pid), {}),
    subjectLabel: labelOf,
  }), [pid]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {profiles.length > 1 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} role="tablist" aria-label="Choose a child">
          {profiles.map((p) => (
            <button key={p.id || 'first'} role="tab" aria-selected={p.id === pid} onClick={() => setChildId(p.id)}
              style={{ padding: '6px 12px', borderRadius: 99, border: `1.5px solid ${p.id === pid ? T.accent : T.border}`, background: p.id === pid ? T.accentLo : T.surface, color: T.text, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              {p.avatar} {p.name}
            </button>
          ))}
        </div>
      )}

      <Card style={{ padding: '18px 20px' }}>
        <p style={{ fontSize: 11, fontWeight: 800, color: T.textDim, letterSpacing: '.08em', textTransform: 'uppercase', margin: '0 0 4px' }}>
          {child?.name ? `${child.name}'s week` : 'This week'} · {fmtDate(r.from)} – {fmtDate(r.to)}
        </p>
        <p style={{ fontSize: 17, fontWeight: 700, color: T.text, margin: '0 0 14px', lineHeight: 1.45 }}>{r.headline}</p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Stat value={r.questions} label="questions answered" />
          <Stat value={r.accuracy != null ? `${r.accuracy}%` : '—'} label="accuracy" />
          <Stat value={r.minutes ? `${r.minutes} min` : `${r.sessions}`} label={r.minutes ? 'time practising' : 'sessions'} />
          <Stat value={`${r.streak} day${r.streak === 1 ? '' : 's'}`} label="daily-goal streak" />
        </div>
      </Card>

      {r.bySubject.length > 0 && (
        <Card style={{ padding: '16px 20px' }}>
          <p style={{ fontSize: 14, fontWeight: 800, color: T.text, margin: '0 0 10px' }}>By subject</p>
          {r.bySubject.map((s) => (
            <div key={s.subject} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: T.text, marginBottom: 4 }}>
                <span style={{ fontWeight: 700 }}>{s.label}</span>
                <span>{s.questions} questions · {s.accuracy}%{s.change != null && s.change !== 0 ? <span style={{ color: s.change > 0 ? T.green : T.red, fontWeight: 700 }}> ({s.change > 0 ? '+' : ''}{s.change} vs last week)</span> : null}</span>
              </div>
              <ProgressBar value={s.accuracy || 0} max={100} color={s.accuracy >= 80 ? T.green : s.accuracy >= 60 ? T.gold : T.red} height={6} />
            </div>
          ))}
        </Card>
      )}

      <Card style={{ padding: '16px 20px' }}>
        <p style={{ fontSize: 14, fontWeight: 800, color: T.text, margin: '0 0 8px' }}>✨ Glow Up — turning mistakes into skills</p>
        <p style={{ fontSize: 13, color: T.textMid, margin: 0, lineHeight: 1.6 }}>
          This week: <b>{r.glow.saved}</b> new mistake{r.glow.saved === 1 ? '' : 's'} saved · <b>{r.glow.fixed}</b> fixed · <b>{r.glow.shining}</b> mastered.
          {r.glow.waiting > 0 ? ` ${r.glow.waiting} waiting to be fixed.` : ' Nothing waiting.'}
        </p>
      </Card>

      {(r.focus.length > 0 || r.strengths.length > 0) && (
        <Card style={{ padding: '16px 20px' }}>
          {r.strengths.length > 0 && <p style={{ fontSize: 13, color: T.text, margin: '0 0 6px' }}>💪 <b>Going well:</b> {r.strengths.map((t) => `${t.topic} (${t.accuracy}%)`).join(', ')}</p>}
          {r.focus.length > 0 && <p style={{ fontSize: 13, color: T.text, margin: 0 }}>🎯 <b>Worth practising:</b> {r.focus.map((t) => `${t.topic} (${t.accuracy}%)`).join(', ')}</p>}
        </Card>
      )}

      <Card style={{ padding: '14px 20px', background: T.accentLo, borderColor: `${T.accent}44` }}>
        <p style={{ fontSize: 13, color: T.text, margin: 0 }}><b>Next step:</b> {r.next}</p>
      </Card>
    </div>
  );
}
